export const NODE_WIDTH = 210;
export const NODE_HEIGHT = 76;
export const EPIC_GAP = 24;
export const TASK_GAP = 12;
export const PROJECT_WIDTH = 440;
export const PROJECT_HEIGHT = 100;
export const PROJECT_GAP = 100;
export const PADDING = 48;

export const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export const normalizeNodes = (nodes) => {
  const unique = [];
  const ids = new Set();
  nodes.forEach((node) => {
    if (!ids.has(node.id)) {
      ids.add(node.id);
      unique.push({ ...node });
    }
  });

  const byId = new Map(unique.map((node) => [node.id, node]));
  return unique.map((node) => {
    let parentId = ids.has(node.parent_id) && node.parent_id !== node.id ? node.parent_id : null;
    const visited = new Set([node.id]);
    let ancestorId = parentId;
    while (ancestorId) {
      if (visited.has(ancestorId)) {
        parentId = null;
        break;
      }
      visited.add(ancestorId);
      ancestorId = byId.get(ancestorId)?.parent_id || null;
    }
    return { ...node, parent_id: parentId };
  });
};

export const normalizeAnalyzedWbs = (title, nodes) => {
  const normalizedNodes = normalizeNodes(nodes);
  const roots = normalizedNodes.filter((node) => !node.parent_id);
  const projectTitle = title || "Work Breakdown Structure";

  if (roots.length !== 1) {
    return { title: projectTitle, nodes: normalizedNodes };
  }

  const root = roots[0];
  const children = normalizedNodes.filter((node) => node.parent_id === root.id);
  const genericTitle = projectTitle === "Work Breakdown Structure";
  const titlesMatch = projectTitle.toLowerCase().trim() === root.title.toLowerCase().trim();
  if (!children.length || (!genericTitle && !titlesMatch)) {
    return { title: projectTitle, nodes: normalizedNodes };
  }

  return {
    title: genericTitle ? root.title : projectTitle,
    nodes: normalizedNodes
      .filter((node) => node.id !== root.id)
      .map((node) => node.parent_id === root.id ? { ...node, parent_id: null } : node)
  };
};

export const isDescendant = (nodes, candidateId, parentId) => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  let current = byId.get(candidateId);
  while (current?.parent_id) {
    if (current.parent_id === parentId) return true;
    current = byId.get(current.parent_id);
  }
  return false;
};

export const reorderWbsNode = (nodes, draggedId, targetId, placement) => {
  const working = nodes.map((node) => ({ ...node }));
  const dragged = working.find((node) => node.id === draggedId);
  const target = working.find((node) => node.id === targetId);
  if (!dragged || !target || dragged.id === target.id || isDescendant(working, target.id, dragged.id)) {
    return working;
  }
  const withoutDragged = working.filter((node) => node.id !== dragged.id);
  if (placement === "child") {
    dragged.parent_id = target.id;
    const lastChildIndex = withoutDragged.reduce(
      (latest, node, index) => node.parent_id === target.id ? index : latest,
      withoutDragged.findIndex((node) => node.id === target.id),
    );
    withoutDragged.splice(lastChildIndex + 1, 0, dragged);
    return withoutDragged;
  }
  dragged.parent_id = target.parent_id || null;
  const targetIndex = withoutDragged.findIndex((node) => node.id === target.id);
  withoutDragged.splice(targetIndex + (placement === "after" ? 1 : 0), 0, dragged);
  return withoutDragged;
};

export const buildWbsLayout = (nodes) => {
  const children = new Map();
  nodes.forEach((node) => children.set(node.id, []));
  nodes.forEach((node) => {
    if (node.parent_id && children.has(node.parent_id)) children.get(node.parent_id).push(node);
  });
  const roots = nodes.filter((node) => !node.parent_id || !children.has(node.parent_id));
  const width = Math.max(1200, PADDING * 2 + roots.length * NODE_WIDTH + Math.max(0, roots.length - 1) * EPIC_GAP);
  const epicY = PADDING + PROJECT_HEIGHT + PROJECT_GAP;
  const project = { x: (width - PROJECT_WIDTH) / 2, y: PADDING };
  const items = [];
  const connectorPaths = [];
  let maxColumnItems = 1;

  const epicStartX = (width - (roots.length * NODE_WIDTH + Math.max(0, roots.length - 1) * EPIC_GAP)) / 2;
  const busY = epicY - 42;
  if (roots.length) {
    connectorPaths.push([
      { x: width / 2, y: project.y + PROJECT_HEIGHT },
      { x: width / 2, y: busY }
    ]);
    if (roots.length > 1) {
      connectorPaths.push([
        { x: epicStartX + NODE_WIDTH / 2, y: busY },
        { x: epicStartX + (roots.length - 1) * (NODE_WIDTH + EPIC_GAP) + NODE_WIDTH / 2, y: busY }
      ]);
    }
  }

  roots.forEach((epic, epicIndex) => {
    const x = epicStartX + epicIndex * (NODE_WIDTH + EPIC_GAP);
    items.push({ node: epic, x, y: epicY, kind: "epic", depth: 0 });
    connectorPaths.push([
      { x: x + NODE_WIDTH / 2, y: busY },
      { x: x + NODE_WIDTH / 2, y: epicY }
    ]);

    const descendants = [];
    const collect = (node, depth) => {
      (children.get(node.id) || []).forEach((child) => {
        descendants.push({ node: child, depth });
        collect(child, depth + 1);
      });
    };
    collect(epic, 1);
    maxColumnItems = Math.max(maxColumnItems, descendants.length + 1);
    descendants.forEach(({ node, depth }, taskIndex) => {
      const y = epicY + NODE_HEIGHT + TASK_GAP + taskIndex * (NODE_HEIGHT + TASK_GAP);
      items.push({ node, x, y, kind: "task", depth });
      connectorPaths.push([
        { x: x + NODE_WIDTH / 2, y: y - TASK_GAP },
        { x: x + NODE_WIDTH / 2, y }
      ]);
    });
  });

  const height = Math.max(560, epicY + maxColumnItems * NODE_HEIGHT + Math.max(0, maxColumnItems - 1) * TASK_GAP + PADDING);
  return { items, connectorPaths, width, height, project };
};

export const roundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
};

export const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(" ");
  let line = "";
  words.forEach((word) => {
    const testLine = `${line}${word} `;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      ctx.fillText(line, x, y);
      line = `${word} `;
      y += lineHeight;
    } else {
      line = testLine;
    }
  });
  ctx.fillText(line, x, y);
};

export const drawCenteredWrappedText = (ctx, text, x, y, width, lineHeight) => {
  ctx.textAlign = "center";
  drawWrappedText(ctx, text, x + width / 2, y, width - 28, lineHeight);
  ctx.textAlign = "start";
};

export const slugify = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const serializeWbs = (title, nodes) => JSON.stringify({ title, nodes });

export const jobStatusMessage = (job, label) => {
  if (job.status === "completed") return `${label} completed.`;
  if (job.status === "failed") return job.error_message || `${label} failed.`;
  return `${label}: ${job.current_step || "Queued"} (${job.progress || 0}%).`;
};
